/**
 * Cloud-relay session tools for the MCP server.
 *
 * These tools proxy browser control through the cloud API via WebSocket.
 *
 * @module
 */

import { readStoredToken } from '../auth';
import { readBrandEnv } from '../brand.js';
import { DEFAULT_API_URL } from '../default-urls.js';
import { LocalBrowserHost } from '../local-browser/host';
import type { LocalBrowserMode } from '../local-browser/protocol';
import { toolJson } from './tool-helpers';

type LocalBrowserStartInput = {
  mode?: LocalBrowserMode;
  cdpUrl?: string;
  headless?: boolean;
  storageStatePath?: string;
  instructions?: string;
};

type LocalBrowserStopInput = {
  sessionId: string;
};

type LocalBrowserStatusInput = {
  sessionId: string;
};

interface BrowserSession {
  sessionId: string;
  host: LocalBrowserHost;
  startedAt: number;
  mode: LocalBrowserMode;
}

// Global browser sessions managed by MCP
const browserSessions = new Map<string, BrowserSession>();

function resolveApiUrl(input?: string): string {
  return input ?? readBrandEnv('CANARY_API_URL') ?? DEFAULT_API_URL;
}

async function resolveToken(): Promise<string> {
  const token = readBrandEnv('CANARY_API_TOKEN') ?? (await readStoredToken());
  if (!token) {
    throw new Error('Missing token. Run `canary login` first or set CANARY_API_TOKEN.');
  }
  return token;
}

export function getSessionToolDefinitions() {
  return [
    {
      name: 'local_browser_start',
      description:
        'Start a local browser session that connects to the cloud agent. The cloud agent can then control this browser to test local applications. Returns sessionId for tracking.',
      inputSchema: {
        type: 'object',
        properties: {
          mode: {
            type: 'string',
            enum: ['playwright', 'cdp'],
            description:
              "Browser mode: 'playwright' for fresh browser, 'cdp' to connect to existing Chrome",
          },
          cdpUrl: {
            type: 'string',
            description: "CDP endpoint URL when mode is 'cdp' (e.g. http://localhost:9222)",
          },
          headless: {
            type: 'boolean',
            description: 'Run browser headless (default: true for playwright mode)',
          },
          storageStatePath: {
            type: 'string',
            description: 'Path to Playwright storage state JSON for pre-authenticated sessions',
          },
          instructions: {
            type: 'string',
            description: 'Instructions for the cloud agent on what to test',
          },
        },
      },
    },
    {
      name: 'local_browser_status',
      description: 'Check the status of a local browser session.',
      inputSchema: {
        type: 'object',
        properties: {
          sessionId: { type: 'string' },
        },
        required: ['sessionId'],
      },
    },
    {
      name: 'local_browser_stop',
      description: 'Stop a local browser session and close the browser.',
      inputSchema: {
        type: 'object',
        properties: {
          sessionId: { type: 'string' },
        },
        required: ['sessionId'],
      },
    },
    {
      name: 'local_browser_list',
      description: 'List all active local browser sessions.',
      inputSchema: {
        type: 'object',
        properties: {},
      },
    },
    {
      name: 'local_browser_run',
      description:
        'Start a test run on an active local browser session. The cloud agent will control the local browser according to the instructions.',
      inputSchema: {
        type: 'object',
        properties: {
          sessionId: {
            type: 'string',
            description: 'The session ID from local_browser_start',
          },
          instructions: {
            type: 'string',
            description: 'Instructions for the cloud agent on what to test',
          },
          startUrl: {
            type: 'string',
            description: 'Optional URL to navigate to before starting',
          },
        },
        required: ['sessionId', 'instructions'],
      },
    },
  ];
}

export async function handleSessionTool(tool: string, args: Record<string, unknown>) {
  const token = await resolveToken();

  if (tool === 'local_browser_start') {
    const input = args as unknown as LocalBrowserStartInput;
    const apiUrl = resolveApiUrl();
    const mode = input.mode ?? 'playwright';

    const sessionResponse = await fetch(`${apiUrl}/local-browser/sessions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        browserMode: mode,
        instructions: input.instructions ?? null,
      }),
    });

    if (!sessionResponse.ok) {
      const text = await sessionResponse.text();
      return toolJson({ ok: false, error: `Failed to create session: ${text}` });
    }

    const session = (await sessionResponse.json()) as {
      ok: boolean;
      sessionId: string;
      wsToken: string;
      expiresAt: string;
    };

    const host = new LocalBrowserHost({
      apiUrl,
      wsToken: session.wsToken,
      sessionId: session.sessionId,
      browserMode: mode,
      cdpUrl: input.cdpUrl,
      headless: input.headless ?? true,
      storageStatePath: input.storageStatePath,
      onLog: (level, message) => {
        if (level === 'error') {
          console.error(`[LocalBrowser] ${message}`);
        }
      },
    });

    host.start().catch((err) => {
      console.error('Failed to start local browser:', err);
      browserSessions.delete(session.sessionId);
    });

    browserSessions.set(session.sessionId, {
      sessionId: session.sessionId,
      host,
      startedAt: Date.now(),
      mode,
    });

    return toolJson({
      ok: true,
      sessionId: session.sessionId,
      mode,
      expiresAt: session.expiresAt,
      note: 'Browser session started. The cloud agent can now control this browser. Use local_browser_stop to end the session.',
    });
  }

  if (tool === 'local_browser_status') {
    const input = args as unknown as LocalBrowserStatusInput;
    const session = browserSessions.get(input.sessionId);

    if (!session) {
      return toolJson({ ok: false, error: 'Session not found', sessionId: input.sessionId });
    }

    return toolJson({
      ok: true,
      sessionId: session.sessionId,
      mode: session.mode,
      startedAt: new Date(session.startedAt).toISOString(),
      uptimeMs: Date.now() - session.startedAt,
    });
  }

  if (tool === 'local_browser_stop') {
    const input = args as unknown as LocalBrowserStopInput;
    const session = browserSessions.get(input.sessionId);

    if (!session) {
      return toolJson({ ok: false, error: 'Session not found', sessionId: input.sessionId });
    }

    await session.host.stop();
    browserSessions.delete(input.sessionId);

    return toolJson({
      ok: true,
      sessionId: input.sessionId,
      note: 'Browser session stopped and browser closed.',
    });
  }

  if (tool === 'local_browser_list') {
    const sessions = Array.from(browserSessions.values()).map((s) => ({
      sessionId: s.sessionId,
      mode: s.mode,
      startedAt: new Date(s.startedAt).toISOString(),
      uptimeMs: Date.now() - s.startedAt,
    }));

    return toolJson({
      ok: true,
      count: sessions.length,
      sessions,
    });
  }

  if (tool === 'local_browser_run') {
    const input = args as unknown as {
      sessionId: string;
      instructions: string;
      startUrl?: string;
    };
    const apiUrl = resolveApiUrl();

    const session = browserSessions.get(input.sessionId);
    if (!session) {
      return toolJson({
        ok: false,
        error: 'Session not found locally. Make sure you started it with local_browser_start.',
        sessionId: input.sessionId,
      });
    }

    const response = await fetch(`${apiUrl}/local-browser/sessions/${input.sessionId}/run`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        instructions: input.instructions,
        startUrl: input.startUrl ?? null,
      }),
    });

    if (!response.ok) {
      const text = await response.text();
      return toolJson({ ok: false, error: `Failed to start run: ${text}` });
    }

    const result = (await response.json()) as { ok: boolean; jobId: string; sessionId: string };

    return toolJson({
      ok: true,
      jobId: result.jobId,
      sessionId: result.sessionId,
      note: 'Test run started. The cloud agent is now controlling your local browser. You can watch the browser to see the test in action.',
    });
  }

  return null; // Not handled
}
